import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ROOT_EFFECTS_INIT } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { of, EMPTY } from 'rxjs';
import { catchError, debounceTime, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';

import { SystemService, UserService } from '@offconon/core-api';

import * as DomainUserActions from './domain-user.actions';
import * as DomainUserSelectors from './domain-user.selectors';

@Injectable()
export class DomainUserEffects {
  private readonly actions$ = inject(Actions);
  private readonly userService = inject(UserService, { optional: true });
  private readonly systemService = inject(SystemService);
  private readonly store = inject(Store);
  private readonly messageService = inject(MessageService);
  private readonly translateService = inject(TranslateService);

  initGuestToken$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ROOT_EFFECTS_INIT),
      withLatestFrom(this.store.select(DomainUserSelectors.selectDomainUserToken)),
      switchMap(([_, userToken]) => {
        if (userToken) {
          return EMPTY;
        }

        return this.systemService.systemGenerateguesttokenCreate().pipe(
          map((settings) =>
            DomainUserActions.loadGuestTokenSuccess({ user_token: settings.user_token ?? '' }),
          ),
          catchError(() => EMPTY),
        );
      }),
    ),
  );

  login$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DomainUserActions.domainUserLogin),
      debounceTime(500),
      switchMap(({ loginRequest }) => {
        if (!this.userService) {
          return of(
            DomainUserActions.domainUserLoginFailure({
              error: 'UserService is not available in this application context.',
            }),
          );
        }

        return this.userService.userLoginCreate(loginRequest).pipe(
          map((loginResponse) => DomainUserActions.domainUserLoginSuccess({ loginResponse })),
          catchError((error) => of(DomainUserActions.domainUserLoginFailure({ error }))),
        );
      }),
    ),
  );

  loginSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DomainUserActions.domainUserLoginSuccess),
        tap(() => {
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('Successful'),
            detail: this.translateService.instant('Login successful'),
          });
        }),
      ),
    { dispatch: false },
  );

  loginFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DomainUserActions.domainUserLoginFailure),
        tap(({ error }) => {
          const detail =
            error?.error?.errors?.[0]?.error ||
            error?.message ||
            this.translateService.instant('Login failed');

          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('Error'),
            detail,
          });
        }),
      ),
    { dispatch: false },
  );

  logout$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DomainUserActions.domainUserLogout),
        tap(() => {
          // localStorageSyncReducer handles clearing localStorage automatically
        }),
      ),
    { dispatch: false },
  );
}
